import {useEffect} from 'react';
import {isClickOutsideExcludedChain} from '../utils/clickOutsideExcludedSelectors';

let onClickOutsideStatus = true;

export const updateOnClickOutsideStatus = (status) => {
    onClickOutsideStatus = status;
};

/**
 * React handles a click before it bubbles up to document, so the clicked node can already be
 * unmounted by the time the listener below runs - e.g. an agent ask option, which disappears
 * the moment it is answered. A detached target belongs to no container any more, so
 * ref.current.contains(event.target) reads such a click as "outside" and closes a popup the
 * user never asked to close. The composed path is computed when the dispatch starts and is not
 * affected by the removal, so it still holds the ancestors the target had while it was attached.
 * It is the full ancestor chain, so membership in it answers both questions below without any
 * further DOM walking.
 */
const useOnClickOutside = (ref, handler, condition = true) => {
    useEffect(
        () => {
            if(!condition) {
                return;
            }

            const isInside = (chain) => chain.includes(ref.current) || isClickOutsideExcludedChain(chain);
            let pressedInside = false;

            // A press inside released outside reaches document as a click on the common ancestor
            const onMouseDown = (event) => {
                pressedInside = !!ref.current && isInside(event.composedPath());
            };
            const onKeyDown = () => {
                pressedInside = false;
            };

            const listener = (event) => {
                const startedInside = pressedInside;
                pressedInside = false;
                if(!ref.current || startedInside) {
                    return;
                }
                if(isInside(event.composedPath())) {
                    return;
                }
                if(onClickOutsideStatus) {
                    handler(event);
                }
            };
            document.addEventListener('mousedown', onMouseDown);
            document.addEventListener('keydown', onKeyDown);
            document.addEventListener('click', listener);
            return () => {
                document.removeEventListener('mousedown', onMouseDown);
                document.removeEventListener('keydown', onKeyDown);
                document.removeEventListener('click', listener);
            };
        },
        [ref, handler, condition]
    );
};

export default useOnClickOutside;
